import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SearchInput } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import { Table, TableBody, TableDisplaySkeletonRows, TablePagination } from '@/enterprise/components/Table';
import { LockedInvoiceFetchOptions } from '@/enterprise/data/useLockedInvoices';
import useLockedInvoiceStats from '@/enterprise/data/useLockedInvoiceStats';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { useCursorPagination } from '@/utils/useCursorPagination';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUrlState from '@/utils/useUrlState';
import NoDataState from '../../NoDataState';
import Skeleton from '../../Skeleton';
import { useInvoiceCentralLayoutContext } from '../InvoiceCentralLayout';
import IneligibleInvoiceFilters, { IneligibleInvoiceFilterOptions } from './IneligibleInvoiceFilters';
import { IneligibleInvoicesTable } from './IneligibleInvoicesTable';
import { mapUrlStateToApiSearchFilters, mergeFilters } from './utils';

type IneligibleInvoiceSortKey = NonNullable<LockedInvoiceFetchOptions>['sortBy'];
export interface IneligibleInvoiceUrlState {
  pageSize: number;
  searchValue?: string;
  filters?: IneligibleInvoiceFilterOptions;
  sort: {
    key: IneligibleInvoiceSortKey;
    direction: 'asc' | 'desc';
  };
}

const IneligibleAmount = ({ urlState }: { urlState: IneligibleInvoiceUrlState }) => {
  const { asCurrency, asNumber } = useLocaleFormat();
  const { t } = useTranslation();

  const {
    buyer: { takerMarkets },
    urlParams: { makerOrganizationUuid, currency },
  } = useInvoiceCentralLayoutContext();

  const search = useMemo(() => mapUrlStateToApiSearchFilters({ urlState }), [urlState]);

  const { data: ineligibleInvoiceStats } = useLockedInvoiceStats({
    makerOrganizationUuid,
    takerMarkets,
    ...(!!search && {
      search,
    }),
  });

  return (
    <div>
      {asCurrency(ineligibleInvoiceStats?.totalAmount ?? 0, currency)} (
      {t('core.invoiceWithCount', {
        count: ineligibleInvoiceStats?.totalCount,
        formattedCount: asNumber({ value: ineligibleInvoiceStats?.totalCount }),
      })}
      )
    </div>
  );
};

const IneligibleInvoices = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState<string>('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const { currentCursor, currentPage, canGoBack, goToNextPage, goToPreviousPage, reset } = useCursorPagination();
  const { addAction } = useDataDog();

  const [urlState, setUrlState] = useUrlState<IneligibleInvoiceUrlState>({
    pageSize: 25,
    sort: { key: 'originalAmount', direction: 'desc' },
  });

  const {
    urlParams: { makerOrganizationUuid, currency },
  } = useInvoiceCentralLayoutContext();

  const handleSearchValue = (search: string | undefined) => {
    addAction('invoice-central::invoices-searched', {
      invoiceType: 'ineligible',
      makerOrganizationUuid,
      currency,
    });

    setUrlState((f) => ({
      ...f,
      searchValue: search?.trim(),
    }));
    reset();
  };

  const handleSort = (sortKey: IneligibleInvoiceUrlState['sort']['key']) => {
    const updatedSortDirection = urlState.sort?.direction === 'asc' ? 'desc' : 'asc';
    addAction('invoice-central::sort::changed', {
      invoiceType: 'ineligible',
      sortKey,
      sortDirection: updatedSortDirection,
      makerOrganizationUuid,
      currency,
    });
    if (urlState.sort && sortKey === urlState.sort.key) {
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: updatedSortDirection,
        },
      }));
    } else {
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: 'desc',
        },
      }));
    }

    reset();
  };

  const handleNextPage = () => {
    if (nextCursor) {
      goToNextPage(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    goToPreviousPage();
  };

  const handleFilters = (newFilters?: IneligibleInvoiceFilterOptions) => {
    addAction('invoice-central::filters::changed', {
      invoiceType: 'ineligible',
      makerOrganizationUuid,
      currency,
      filters: Object.keys(newFilters ?? {}),
    });

    setUrlState((f) => {
      return {
        ...f,
        filters: mergeFilters({ prevFilters: f.filters, newFilters }),
      };
    });
    reset();
  };

  const clearFilters = () => {
    setUrlState((f) => {
      const updatedState: Partial<IneligibleInvoiceUrlState> = {
        ...f,
        filters: undefined,
      };

      return updatedState;
    });
    reset();
  };

  return (
    <div>
      <IneligibleInvoiceFilters
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        handleFilters={handleFilters}
        currency={currency}
        filters={urlState.filters}
      />
      <div className="border-y border-deepGray-500/[12%] bg-canvas">
        <div className="flex flex-col justify-between sm:flex-row">
          {/* Search Bar */}
          <div className="flex flex-col items-center gap-3 p-4 sm:flex-row">
            <SearchInput
              className="w-full sm:w-60"
              onChange={(e) => setSearchInput(e.target.value)}
              onClear={() => {
                setSearchInput('');
                handleSearchValue(undefined);
              }}
              placeholder={t('invoices.searchInputPlaceholder')}
              value={searchInput}
              withSearchIcon
              onKeyDown={(e) => e.key === 'Enter' && handleSearchValue(searchInput)}
            />
            <Button
              className="w-full bg-canvas sm:w-fit"
              onClick={() => handleSearchValue(searchInput)}
              variant="secondary"
            >
              {t('core.search')}
            </Button>
          </div>
          {/* Filters */}
          <div className="flex items-center gap-2 p-4">
            <div className="relative">
              <Button onClick={() => setFilterOpen(true)} variant="primary">
                {t('core.filters')}
              </Button>
              {Object.keys(urlState.filters ?? {}).length > 0 && (
                <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white">
                  {Object.keys(urlState.filters ?? {}).length}
                </div>
              )}
            </div>
            <Button
              disabled={Object.keys(urlState.filters ?? {}).length === 0}
              onClick={clearFilters}
              variant="secondary"
            >
              {t('core.clearFilters')}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1 pb-4 pl-4">
          <div className="font-medium">{t('core.ineligibleAmount')}:</div>
          <QueryBoundaries LoadingComponent={() => <Skeleton className="h-6 w-24" />}>
            <IneligibleAmount urlState={urlState as IneligibleInvoiceUrlState} />
          </QueryBoundaries>
        </div>
      </div>
      <QueryBoundaries
        LoadingComponent={() => (
          <Table>
            <TableBody>
              <TableDisplaySkeletonRows colSpan={4} rowCount={10} />
            </TableBody>
          </Table>
        )}
        ErrorComponent={() => (
          <div className="px-4 pb-6 pt-8">
            <NoDataState
              icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
              title={t('invoices.errorLoadingInvoices')}
              message={t('invoices.errorLoadingInvoicesDescription')}
              action={
                <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                  {t('core.refresh')}
                </Button>
              }
            />
          </div>
        )}
      >
        <IneligibleInvoicesTable
          currentCursor={currentCursor}
          setNextCursor={setNextCursor}
          urlState={urlState as IneligibleInvoiceUrlState}
          handleSort={handleSort}
        />
      </QueryBoundaries>
      <TablePagination
        className="p-6"
        onNextChange={handleNextPage}
        onPrevChange={handlePreviousPage}
        onPageSizeChange={(e) => setUrlState((f) => ({ ...f, pageSize: Number(e.target.value) }))}
        pageSize={urlState.pageSize ?? 25}
        disablePrev={!canGoBack}
        disableNext={!nextCursor}
        currentPage={currentPage}
      />
    </div>
  );
};

export default IneligibleInvoices;
